import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// material-ui
import { 
    Button, DialogContent, Dialog, DialogActions, Grid, Stack, 
    InputLabel, OutlinedInput, DialogTitle, Alert, CircularProgress, FormControlLabel, Switch
} from '@mui/material'

// third party
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useSnackbar } from "notistack";

// project import
import AnimateButton from 'components/@extended/AnimateButton'
import { useApiData } from 'services/hook/useApiData'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { EVENT_CATEGORY_UPDATE_INIT, EVENT_CATEGORY_UPDATE_SUCCESS } from 'models/events/category.event'
import { categoryDetail, categoryEdit } from 'models/category'
import { FormFieldError } from 'components/Form'
import ImageUpload from 'components/common/ImageUpload'


/*
 * Initiate category edit popup
 */
export const CategoryEditDialog = () => {
    const [categoryid, setCategoryid] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_CATEGORY_UPDATE_INIT, (id) => {
            setCategoryid(id)
        })
    }, [eventbus])

    return (
        <Dialog 
            open={categoryid != null} 
            scroll={'body'} 
            maxWidth="md" 
            fullWidth={true} 
            onClose={() => setCategoryid(null)}
        >
            {categoryid && <CategoryEdit 
                categoryid={categoryid} 
                onClose={() => setCategoryid(null)} 
                onSuccess={(data) => {
                    eventbus.fire(EVENT_CATEGORY_UPDATE_SUCCESS, data)
                    setCategoryid(null)
                }} 
            />}
        </Dialog>
    )
}

//Category edit form validation rules
const validation = Yup.object().shape({
    name: Yup.string().max(255, 'Name must be at most 255 characters').required('Name is required'),
    description: Yup.string(),
    is_active: Yup.boolean().required('Status is required'),
    image: Yup.mixed().nullable()
})

/**
 * Category edit form
 */
const CategoryEdit = ({ categoryid, onClose, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)

    const [data, , loading, loadData] = useApiData({ data: {} }, {}, (params, setData) => {
        if (!categoryid) {
            setData({})
            return
        }
        categoryDetail(categoryid).then((res) => {
            const categoryData = res?.data || res || {};
            setData(categoryData)
            if (categoryData.image_url) {
                setImagePreview(categoryData.image_url)
            }
        })
        .catch((res) => {
            enqueueSnackbar(res.message || 'Failed to load category details', { 
                variant: 'error', 
                autoHideDuration: 2000 
            })
            setData({}) // Set empty data on error to prevent loops
        })
    }, false)

    useEffect(() => {
        if (categoryid) {
            loadData({})
        }
    }, [categoryid]) // Remove loadData from deps - it's stable now

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageRemove = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    //Send data to server
    const onSubmit = async (values, { setErrors }) => {
        const submitData = {
            name: values.name,
            description: values.description || '',
            is_active: values.is_active ? 'active' : 'inactive',
            image: imageFile ? [imageFile] : null
        };

        await categoryEdit(categoryid, submitData)
            .then((res) => {
                onSuccess(res.data)
                enqueueSnackbar(res.message || 'Category updated successfully', { 
                    variant: 'success', 
                    autoHideDuration: 1500 
                })
            })
            .catch((res) => {
                if (res.errors) {
                    res.errors['submit'] = res.message
                    setErrors(res.errors)
                } else {
                    setErrors({ submit: res.message })
                }
                enqueueSnackbar(res.message || 'Failed to update category', { 
                    variant: 'error', 
                    autoHideDuration: 1500 
                })
            })
    }

    if (loading) {
        return (
            <>
                <DialogTitle>Edit Category</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </DialogContent>
            </>
        )
    }
    return (
        <Formik initialValues={{ 
            name: data?.name ?? '', 
            description: data?.description ?? '', 
            is_active: data.status == 'active' ? true : false,
            image: null
        }} enableReinitialize={true} validationSchema={validation} onSubmit={onSubmit} >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
                <form noValidate onSubmit={handleSubmit}>
                    <DialogTitle>Edit Category</DialogTitle>

                    <DialogContent dividers>
                        <Grid container spacing={3}>
                            {errors.submit && (
                                <Grid item size={{ xs:12 }} >
                                    <Alert severity="error">{errors.submit}</Alert>
                                </Grid>
                            )}

                            <Grid item size={{ xs:12 }} >
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="category-name">Name *</InputLabel>
                                    <OutlinedInput id="category-name" type="text" value={values.name} name="name" onBlur={handleBlur} onChange={handleChange} placeholder="Enter category name" fullWidth error={Boolean(touched.name && errors.name)} />
                                    <FormFieldError touched={touched.name} errors={errors.name} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs:12 }} >
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="category-description">Description</InputLabel>
                                    <OutlinedInput id="category-description" type="text" value={values.description} name="description" onBlur={handleBlur} onChange={handleChange} placeholder="Enter description" fullWidth multiline rows={3} error={Boolean(touched.description && errors.description)} />
                                    <FormFieldError touched={touched.description} errors={errors.description} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs:12 }} >
                                <Stack spacing={1}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.is_active}
                                                onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                                name="is_active"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    <FormFieldError touched={touched.is_active} errors={errors.is_active} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs:12 }} >
                                <Stack spacing={1}>
                                    <InputLabel>Image</InputLabel>
                                    <ImageUpload
                                        imagePreview={imagePreview}
                                        onImageChange={handleImageChange}
                                        onImageRemove={handleImageRemove}
                                        id="category-image-upload"
                                    />
                                    <FormFieldError touched={touched.image} errors={errors.image} />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 1.5 }}>
                        <AnimateButton>
                            <Button disabled={isSubmitting} onClick={onClose} variant="outlined" color="secondary">
                                Cancel
                            </Button>
                        </AnimateButton>

                        <AnimateButton>
                            <Button disabled={isSubmitting} type="submit" variant="contained" color="primary" >
                                {isSubmitting ? 'Updating...' : 'Update'}
                            </Button>
                        </AnimateButton>
                    </DialogActions>
                </form>
            )}
        </Formik>
    )
}

//CategoryEdit component prop type declaration
CategoryEdit.propTypes = {
    onClose: PropTypes.func,
    onSuccess: PropTypes.func,
    categoryid: PropTypes.number
}

//export default component
export default CategoryEdit

